// Utility parsing functions extracted for unit testing and reuse

export function tryParseTime(input: string): { hours: number; minutes: number; seconds?: number; ms?: number } | null {
	if (typeof input !== "string") return null;
	// Match HH:mm or HH:mm:ss with optional AM/PM
	let match = input.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AaPp][Mm])?/);
	if (match) {
		let hours = parseInt(match[1]);
		const minutes = parseInt(match[2]);
		const seconds = match[3] ? parseInt(match[3]) : 0;
		const ampm = match[4]?.toLowerCase();
		if (ampm) {
			if (ampm === "pm" && hours < 12) hours += 12;
			if (ampm === "am" && hours === 12) hours = 0;
		}
		if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
			return { hours, minutes, seconds, ms: 0 };
		}
		return null;
	}
	// Match h AM/PM
	match = input.match(/\b(\d{1,2})\s*([AaPp][Mm])\b/);
	if (match) {
		let hours = parseInt(match[1]);
		const ampm = match[2]?.toLowerCase();
		// For AM/PM format without minutes, the hour must be 1-12
		if (hours < 1 || hours > 12) return null;
		if (ampm === "pm" && hours < 12) hours += 12;
		if (ampm === "am" && hours === 12) hours = 0;
		return { hours, minutes: 0, seconds: 0, ms: 0 };
	}
	return null;
}

export function tryParseDate(
	dateString: string | Date,
	opts?: { enableDate?: boolean; enableTime?: boolean; dateOnly?: boolean },
): Date | null {
	const enableDate = opts?.enableDate;
	const enableTime = opts?.enableTime;
	const dateOnly = opts?.dateOnly;

	if (typeof dateString === "object") return dateString;

	// A date-only value is a calendar date, so read its yyyy-MM-dd part as local midnight.
	// new Date() would read "1980-05-15T00:00:00Z" as the evening of May 14 in US time zones.
	const isoDate = dateOnly ? /^(\d{4})-(\d{2})-(\d{2})(?:$|T)/.exec(dateString) : null;
	if (isoDate) {
		const year = parseInt(isoDate[1]);
		const month = parseInt(isoDate[2]) - 1;
		const day = parseInt(isoDate[3]);
		const result = new Date(year, month, day);
		return result.getMonth() === month && result.getDate() === day ? result : null;
	}

	try {
		const nativeDate = new Date(dateString);
		if (!isNaN(nativeDate.getTime()) && !/^\d{1,2}[.\-/]\d{1,2}[.\-/]\d{2,4}/.test(dateString)) {
			return nativeDate;
		}

		const time = tryParseTime(dateString);

		// Time-only input handling when calendar is disabled
		if (enableDate === false && enableTime !== false && time) {
			const base = new Date();
			base.setHours(time.hours, time.minutes ?? 0, time.seconds ?? 0, time.ms ?? 0);
			return base;
		}

		const splitDate = dateString.split(/[.\-/]/);
		const currentYear = new Date().getFullYear();
		let year: number;
		let month: number;
		let day: number;
		if (splitDate[2]?.length === 2) {
			year = parseInt(splitDate[2]) + 2000;
			if (year > currentYear) year -= 100;
			month = parseInt(splitDate[0]) - 1;
			day = parseInt(splitDate[1]);
		} else {
			// We have to use the split date because safari only supports / format
			year = parseInt(splitDate[2]);
			month = parseInt(splitDate[0]) - 1;
			day = parseInt(splitDate[1]);
		}
		const result = new Date(year, month, day);
		if (!isNaN(result.getTime())) {
			if (time && enableTime !== false && !dateOnly) {
				result.setHours(time.hours, time.minutes ?? 0, time.seconds ?? 0, time.ms ?? 0);
			}
			return result;
		}
		return null;
	} catch (e) {
		return null;
	}
}
